// Central applyEvent merge per docs/ui/operator-ui-tds.md §5.3.
//
// All incoming Socket.IO events from the operator BFF flow through this single
// function and update the TanStack Query cache via setQueryData. Never refetch
// on a WS event — a refetch storm during a hub closure would saturate the BFF
// (TDS §14 anti-pattern 2).
//
// Adding a new event type means adding a handler here. Don't sprinkle
// setQueryData calls across feature components.

import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import type { LockState } from '@/lib/locks/types';
import type {
  CareBoard,
  DisruptionPnrGroupRow,
  DisruptionProgress,
  DisruptionTripsPage,
  ITransferBoard,
  RebookingDetailView,
  RebookingGroupResult,
  RebookingSettleOutcome,
  ShuttleRunView,
  TransferPlanView,
} from '@/lib/disruptions/types';
import type { AllocationPlanEnvelope } from '@/lib/bulk-allocation/types';
import {
  compensationRoot,
  mergeCompensationChange,
  scheduleCompensationRefresh,
} from '@/lib/compensation/merge';
import type { CompensationChangedEvent, ICompensationProposal } from '@/lib/compensation/types';
import { statusDeltaFor } from '@/lib/flights/status-source';
import type { IFlightMonitorRow } from '@/lib/flights/types';
import { caseTripsKey, mergeTrip } from '@/lib/transport/trips';
import type { TripView } from '@/lib/transport/types';
import { groupVoucherKey, type GroupVoucherView } from '@/lib/transport/voucher-types';
import { isWhyQueryOfDisruption, toChapter, upsertChapter } from '@/lib/why/merge';
import type { WhyView } from '@/lib/why/types';
import {
  applyFlightDelayChanged,
  applyFlightEscalated,
  invalidateEntitlementsOfGroup,
  isFlightDelayChanged,
  isFlightEscalated,
} from '@/lib/delay/merge';
import type { FlightDelayChangedEvent, FlightEscalatedEvent } from '@/lib/delay/types';
import { applyEscalationArrived, applyEscalationProgress } from '@/lib/airline-reassignment/merge';
import { applyIntegrationChanged, readIntegrationChange } from '@/lib/providers/integration-changed';
import type { IntegrationChange } from '@/lib/providers/types';
import { isOperatorAlertV1, type OperatorAlertEvent } from '@/lib/alerts/alert.types';

type RosterPage = { items: DisruptionPnrGroupRow[]; nextCursor?: string | null };

/** `operator-bff:case-changed.booking-status@v1` — what the reservation event said. */
export type CaseChangedReservationStatus = 'HELD' | 'CONFIRMED' | 'FAILED' | 'RELEASED' | 'CANCELLED';

/** A reservation that ended: the row's reservation is cleared, not kept as «reserved». */
const RESERVATION_GONE: ReadonlySet<string> = new Set(['FAILED', 'RELEASED', 'CANCELLED']);

/**
 * One roster row with the reservation event applied (pure — exported for its test).
 * An ENDED reservation (FAILED / RELEASED / CANCELLED) clears the row only when the event
 * names that very reservation (`endedReservationUrn`, §5 amendment — never
 * `reservationUrn`, which a pre-§5 UI reads as «booked»). An ended event naming none (an
 * older BFF) never touches the row — the PNR may well hold another reservation (an orphan
 * hold released by the sweep, a retried issue that failed); the caller re-reads instead.
 */
export function withHotelDenorm(
  g: DisruptionPnrGroupRow,
  reservationUrn: string | undefined,
  hotelUrn: string | undefined,
  status: CaseChangedReservationStatus | undefined,
): DisruptionPnrGroupRow {
  if (status && RESERVATION_GONE.has(status)) {
    if (!reservationUrn || g.hotelReservationUrn !== reservationUrn) return g;
    const rest = { ...g };
    delete rest.hotelReservationUrn;
    delete rest.hotelReservationStatus;
    return rest;
  }
  if (!reservationUrn) return g;
  const same = g.hotelReservationUrn === reservationUrn;
  // HELD → CONFIRMED only moves forward for the same reservation (a late HELD after
  // its CONFIRMED is stale). No status (an older BFF) keeps the legacy patch as it was.
  const held = status === 'HELD' || status === 'CONFIRMED' ? status : undefined;
  const next =
    held === 'HELD' && same && g.hotelReservationStatus === 'CONFIRMED'
      ? 'CONFIRMED'
      : (held ?? g.hotelReservationStatus);
  const out: DisruptionPnrGroupRow = { ...g, hotelReservationUrn: reservationUrn, ...(hotelUrn ? { hotelUrn } : {}) };
  if (next) out.hotelReservationStatus = next;
  else delete out.hotelReservationStatus;
  return out;
}

/**
 * A cached PNR roster: a disruption's (`['disruptions', urn, 'roster', …]`) or a case's
 * (`['cases', urn, 'pnr-groups', …]`, the case detail's Passengers tab). Both hold
 * `{ items, nextCursor }` pages of the same rows.
 */
function isPnrRosterQuery(q: { queryKey: readonly unknown[] }): boolean {
  return (
    (q.queryKey[0] === 'disruptions' && q.queryKey[2] === 'roster') ||
    (q.queryKey[0] === 'cases' && q.queryKey[2] === 'pnr-groups')
  );
}

/**
 * Patch the hotel denorm onto a PNR's roster row across any cached PNR roster —
 * disruption or case (#872: the case's drawer reads its row to offer the transport
 * voucher) — so the GRID moves the moment a reservation event lands, without waiting
 * on the async cases denorm (which has no refresh trigger) + a refetch that would
 * race it. Keyed by `subCaseUrn`; a no-op on rosters that don't hold it. The
 * event's `reservationStatus` (§5) sets the row's status — HELD reads as «in
 * progress», never as a confirmed ✓ — and an ended reservation clears the row.
 */
function patchRosterHotelDenorm(
  qc: QueryClient,
  subCaseUrn: string,
  reservationUrn: string | undefined,
  hotelUrn?: string,
  status?: CaseChangedReservationStatus,
): void {
  qc.setQueriesData<InfiniteData<RosterPage>>(
    { predicate: isPnrRosterQuery },
    (data) => {
      if (!data?.pages) return data;
      return {
        ...data,
        pages: data.pages.map((p) => ({
          ...p,
          items: p.items.map((g) =>
            g.subCaseUrn === subCaseUrn ? withHotelDenorm(g, reservationUrn, hotelUrn, status) : g,
          ),
        })),
      };
    },
  );
}

type FlightMonitorPage = { items: IFlightMonitorRow[]; nextCursor?: string | null };

/**
 * Merge a live `flight.status-changed` delta into EVERY cached flights-monitor
 * walk (NEX-343 / F5). The monitor query key is `['flights','monitor', …filters]`
 * — a filter change starts a fresh walk, so we patch across all of them by
 * prefix. Keyed by `flightUrn`; rows the delta doesn't name are untouched, and a
 * delta for a flightUrn not in any loaded page is a harmless no-op (the row
 * arrives enriched on its own page fetch). Never a refetch (TDS §14).
 *
 * The airline wins the status (synthetic-airline §6 inv. 4): while a row's status
 * is the airline's, a non-airline (or older airline) delta merges WITHOUT its
 * status fields — `statusDeltaFor` in lib/flights decides.
 */
function patchFlightMonitorRow(
  qc: QueryClient,
  flightUrn: string,
  delta: Partial<IFlightMonitorRow>,
): void {
  qc.setQueriesData<InfiniteData<FlightMonitorPage>>(
    { predicate: (q) => q.queryKey[0] === 'flights' && q.queryKey[1] === 'monitor' },
    (data) => {
      if (!data?.pages) return data;
      return {
        ...data,
        pages: data.pages.map((p) => ({
          ...p,
          items: p.items.map((row) =>
            row.flightUrn === flightUrn ? { ...row, ...statusDeltaFor(row, delta) } : row,
          ),
        })),
      };
    },
  );
}

/**
 * rebooking-progress-transparency (NEX-364 / U1) — merge the per-PNR settle
 * outcomes carried on a live progress push (`disruption-progress@v2`) into the
 * rebooking-detail cache, so the rich per-PNR rows FILL IN as groups settle over
 * the WS (never a poll/refetch). Upsert by `groupId` (a settle overlays the REST
 * seed, keeping the freshest fields); a group that settled earlier is preserved
 * even if a later snapshot omits it. A no-op when nothing settled yet.
 */
function mergeSettleOutcomesIntoDetail(
  qc: QueryClient,
  urn: string,
  outcomes: RebookingSettleOutcome[] | undefined,
): void {
  if (!outcomes || outcomes.length === 0) return;
  qc.setQueryData<RebookingDetailView>(['disruptions', urn, 'rebooking-detail'], (prev) => {
    const base: RebookingDetailView = prev ?? {
      urn,
      groups: [],
      computedAt: new Date().toISOString(),
    };
    const byId = new Map<string, RebookingGroupResult>(base.groups.map((g) => [g.groupId, g]));
    for (const o of outcomes) {
      const existing = byId.get(o.groupId);
      const options = o.options.length > 0 ? o.options : (existing?.options ?? []);
      byId.set(o.groupId, {
        groupId: o.groupId,
        pnrMasked: o.pnrMasked || existing?.pnrMasked || '—',
        paxCount: o.paxCount ?? existing?.paxCount ?? 0,
        status: o.status,
        optionCount: options.length,
        options,
        ...(o.noOptionsReason ? { noOptionsReason: o.noOptionsReason } : {}),
        ...(o.best ?? existing?.best ? { best: o.best ?? existing?.best } : {}),
        ...(o.scored ?? existing?.scored ? { scored: o.scored ?? existing?.scored } : {}),
        ...(o.original ?? existing?.original ? { original: o.original ?? existing?.original } : {}),
        // progress-settle@v3 — the settle facts are the LATEST run's: copied as pushed
        // (a re-run that dropped the accept must not keep the old one).
        ...(o.settledAt ? { settledAt: o.settledAt } : {}),
        ...(o.settleOutcome ? { settleOutcome: o.settleOutcome } : {}),
        ...(o.accept ? { accept: o.accept } : {}),
        ...(o.assigned ? { assigned: o.assigned } : {}),
      });
    }
    return { ...base, groups: [...byId.values()] };
  });
}

const TRANSFER_BOARD = 'transfers';
/** Cache segment of the disruption-grain trips read (`disruptionTripsKey`). */
const TRIPS_PAGE = 'trips';

/** Upsert one shuttle run into the cached transfer board (by run urn, stable order). */
function mergeTransferRun(qc: QueryClient, urn: string, run: ShuttleRunView): void {
  const key = ['disruptions', urn, TRANSFER_BOARD];
  const prev = qc.getQueryData<ITransferBoard | null>(key);
  if (prev === undefined) return; // no board open — nothing to patch, never seed a partial one
  qc.setQueryData<ITransferBoard | null>(key, (cur) => {
    const base: ITransferBoard = cur ?? { plan: null, runs: [], hotels: [], facts: emptyFacts(), known: true };
    const idx = base.runs.findIndex((r) => r.urn === run.urn);
    const runs =
      idx >= 0
        ? base.runs.map((r, i) => (i === idx ? { ...r, ...run } : r))
        : [...base.runs, run].sort(
            (a, b) =>
              a.hotelUrn.localeCompare(b.hotelUrn) || a.leg.localeCompare(b.leg) || a.seq - b.seq,
          );
    return { ...base, runs };
  });
}

/** Replace the cached plan + refresh the per-hotel plan counters the board rows carry. */
function mergeTransferPlan(qc: QueryClient, urn: string, plan: TransferPlanView): void {
  const key = ['disruptions', urn, TRANSFER_BOARD];
  const prev = qc.getQueryData<ITransferBoard | null>(key);
  if (prev === undefined) return;
  qc.setQueryData<ITransferBoard | null>(key, (cur) => {
    const base: ITransferBoard = cur ?? { plan: null, runs: [], hotels: [], facts: emptyFacts(), known: true };
    const byUrn = new Map(plan.hotels.map((h) => [h.hotelUrn, h] as const));
    const hotels = base.hotels.map((h) => {
      const p = byUrn.get(h.hotelUrn);
      return p ? { ...h, ...p, hotelName: h.hotelName || p.hotelName || h.hotelUrn } : h;
    });
    // Hotels the plan knows and the roster row list does not (yet): append them.
    for (const p of plan.hotels) {
      if (!hotels.some((h) => h.hotelUrn === p.hotelUrn))
        hotels.push({ ...p, hotelName: p.hotelName ?? p.hotelUrn, paxNeeded: 0, pnrs: 0 });
    }
    return { ...base, plan, hotels };
  });
}

/**
 * transport-desk (T3) — upsert one pushed `TripChanged` payload into the cached
 * disruption-grain trips page (`['disruptions', urn, 'trips']`). Only patches an
 * EXISTING page (never seeds one, and never overwrites the `null` "route not
 * wired" marker); the merge is the same whole-object replace the case list uses.
 */
function mergeDisruptionTrip(qc: QueryClient, urn: string, trip: TripView): void {
  const key = ['disruptions', urn, TRIPS_PAGE];
  const prev = qc.getQueryData<DisruptionTripsPage | null>(key);
  if (prev === undefined || prev === null) return;
  qc.setQueryData<DisruptionTripsPage | null>(key, (cur) =>
    cur ? { ...cur, trips: mergeTrip(cur.trips, trip) } : cur,
  );
}

function emptyFacts(): ITransferBoard['facts'] {
  return {
    needed: 0,
    enrolled: 0,
    planned: 0,
    dispatched: 0,
    enRoute: 0,
    arrived: 0,
    noShow: 0,
    ownMeans: 0,
    failed: 0,
    hotels: 0,
    vehiclesPlanned: 0,
    vehiclesOnRoad: 0,
    returnScheduled: 0,
    returnOnRoad: 0,
    returnDone: 0,
    known: false,
  };
}

/**
 * Patch one `CareChanged` card slice into the cached care board (feat/care-desk
 * K5). Only writes into an EXISTING board (no partial seeding); patches only the
 * fields the slice carries; a settled CREDIT clears the "settling" chip. The
 * accumulator column (`toppedUpMinor`) is NEVER bumped client-side — redelivery
 * must land on identical state, so the invalidate that follows owns that truth.
 */
function mergeCareCard(
  qc: QueryClient,
  urn: string,
  subCaseUrn: string,
  card: NonNullable<CareChangedEvent['card']>,
): void {
  const key = ['disruptions', urn, 'care'];
  if (qc.getQueryData<CareBoard>(key) === undefined) return;
  qc.setQueryData<CareBoard>(key, (prev) => {
    if (!prev) return prev;
    const credited = card.lastAdjustment?.direction === 'CREDIT';
    return {
      ...prev,
      cards: prev.cards.map((row) =>
        row.subCaseUrn === subCaseUrn
          ? {
              ...row,
              ...(card.status ? { status: card.status } : {}),
              ...(card.currency ? { currency: card.currency } : {}),
              ...(typeof card.faceValueMinor === 'number'
                ? { faceValueMinor: card.faceValueMinor }
                : {}),
              ...(typeof card.availableMinor === 'number'
                ? { availableMinor: card.availableMinor }
                : {}),
              ...(credited ? { pendingTopUp: false } : {}),
            }
          : row,
      ),
    };
  });
}

export interface StateChangedEvent {
  type: 'StateChanged';
  caseUrn: string;
  payload: {
    locked_by: LockState['holder'];
    lock_expires_at: LockState['expiresAt'];
    [key: string]: unknown;
  };
}

export interface PassengerStatusChangedEvent {
  type: 'PassengerStatusChanged';
  caseUrn: string;
  passengerUrn: string;
  payload: Record<string, unknown>;
}

export interface AllocationProgressEvent {
  type: 'AllocationProgress';
  caseUrn: string;
  payload: Record<string, unknown>;
}

export interface BookingConfirmedEvent {
  type: 'BookingConfirmed';
  caseUrn: string;
  reservationUrn: string;
  payload: Record<string, unknown>;
}

// Live cross-user domain change (booking / rebooking / allocation / passenger).
// The operator-bff emits this right after it successfully proxies a write, so
// every OTHER operator viewing the SAME case refetches just the affected slice
// — no manual refresh. Carries only WHAT changed (`kind`), not the new state.
export type CaseChangeKind = 'booking' | 'rebooking' | 'allocation' | 'passenger';

export interface CaseChangedEvent {
  type: 'CaseChanged';
  caseUrn: string;
  kind: CaseChangeKind;
  // Present when the write was scoped to a single passenger group.
  subCaseUrn?: string;
  // On a confirmed booking (kind:'booking'): the reservation + hotel for `subCaseUrn`,
  // so we patch that PNR's roster row hotel denorm live (the grid otherwise waits on
  // the async cases denorm with no refresh trigger).
  reservationUrn?: string;
  hotelUrn?: string;
  // `case-changed.booking-status@v1` (additive): which reservation event this was, the
  // hotel's name and the envelope's produced_at. Absent on an older BFF.
  reservationStatus?: CaseChangedReservationStatus;
  hotelName?: string;
  at?: string;
  // §5 amendment (review round 2): on FAILED / RELEASED / CANCELLED only, the reservation
  // that ENDED — never sent as `reservationUrn` (an older UI patches that as live).
  endedReservationUrn?: string;
}

// Live disruption progress + run-state push (NEX-293). The operator-bff fans a
// fresh progress snapshot to the `disruption:<urn>` room whenever the rollup
// counters or the bulk `activeRun` change. The control center subscribes to it
// (instead of polling) and the merge below writes the snapshot straight into
// the `['disruptions', urn, 'progress']` query cache — never a refetch.
export interface DisruptionProgressChangedEvent {
  type: 'DisruptionProgressChanged';
  disruptionUrn: string;
  payload: DisruptionProgress;
}

// The SHARED event allocation plan changed — another operator built/booked it.
// The BFF pushes the full envelope to the disruption room; the merge writes it
// straight into the allocation-plan cache so it appears live for everyone.
export interface DisruptionAllocationPlanChangedEvent {
  type: 'DisruptionAllocationPlanChanged';
  disruptionUrn: string;
  payload: AllocationPlanEnvelope;
}

// Live per-vendor health push (D). The BFF fans a fresh provider-status snapshot
// to the global `providers` room whenever a vendor's health changes; the merge
// writes it straight into `['providers', 'status']` — never a refetch.
export interface ProviderStatusChangedEvent {
  type: 'ProviderStatusChanged';
  payload: unknown;
}

// A toggle / mode / routing write landed (providers-by-service B2 → U2), fanned
// to the global `providers` room. The merge lives in lib/providers/integration-changed.
export type IntegrationChangedEvent = { type: 'IntegrationChanged' } & IntegrationChange;

// Live flight-status push (NEX-343 / F5). The BFF (FlightsGateway) relays the
// flight-predictor SSE `flight.status-changed` stream to the global `flights`
// room, keyed by `flightUrn`, with the changed fields spread inline (e.g.
// `status`, `expectedDelayMinutes`). The merge patches just that row in the
// `['flights','monitor']` cache — never a refetch (replaces the F4 poll). The
// envelope is flat (matches the BFF emit): `{ flightUrn, status?, ...delta }`.
export interface FlightStatusChangedEvent {
  type: 'flight.status-changed';
  flightUrn: string;
  // Any subset of IFlightMonitorRow fields the BFF chose to push (status,
  // expectedDelayMinutes, cancelProbability, …). Additive — ignore unknown keys.
  [key: string]: unknown;
}

/**
 * `operator-bff:operator-notification@v1` (NEX-296) — the GLOBAL, operator-scoped
 * notification envelope. UI mirror of the FROZEN wire contract declared in
 * `apps/operator-bff/src/modules/locks/cases.gateway.ts`; do not re-derive it here.
 *
 * It arrives on the SAME `/cases` namespace as every other event, so `socket.ts`
 * needs no change: the socket auto-joins `operator:<operatorUrn>` at connect time
 * from its VERIFIED handshake identity, and there is deliberately no `join:` for
 * this room — a room the client has to ask for lapses on navigation, and this
 * channel exists precisely to survive the operator walking away from the screen
 * that started the run.
 *
 * `title` / `message` are composed server-side and are rendered VERBATIM; the
 * numeric `run.*` fields ride along for a detail line + test assertions. Never
 * recompose the sentence from the numbers.
 */
export interface IOperatorNotificationV1 {
  /** Stable dedupe key, `${runId}:${status}` — surface once. */
  id: string;
  /** v1 discriminator; only 'run' exists today. Switch on it, never assume it. */
  kind: 'run';
  severity: 'success' | 'warning' | 'error';
  /** Pre-composed headline (server-side, EN). */
  title: string;
  /** Pre-composed one-line body (server-side, EN). */
  message: string;
  /** Recipient — same value as the room key. */
  operatorUrn: string;
  /** ISO-8601 UTC. */
  occurredAt: string;
  run: {
    runId: string;
    op: 'rebooking' | 'allocation';
    /** Terminal only — never RUNNING/QUEUED/NONE. */
    status: 'COMPLETED' | 'PARTIAL' | 'FAILED';
    disruptionUrn: string;
    processedCases: number;
    totalCases: number;
    processedGroups: number;
    plannedGroups: number;
    degradedGroups: number;
  };
  /** Deep-link back to the run's disruption (an operator-ui route path). */
  link: { label: string; href: string };
}

export interface OperatorNotificationEvent {
  type: 'OperatorNotification';
  operatorUrn: string;
  payload: IOperatorNotificationV1;
}

/**
 * `operator-bff:WS TripChanged@v1` (feat/transport T4/T5) — one ground-transport
 * trip changed state. Fanned into the `case:<urn>` room (and `disruption:<urn>`
 * when the producer knew it), from BOTH the domain's `{tenant}.transport.trip-*`
 * Kafka stream and the BFF's own write routes, so the operator's card is live
 * whether the movement came from a vendor webhook or the operator next to them.
 *
 * The payload carries the WHOLE `TripView`, which is what makes duplicate
 * delivery (Kafka echo + write echo) harmless: both land on identical state.
 */
export interface TripChangedEvent {
  type: 'TripChanged';
  caseUrn: string;
  trip: TripView;
}

/**
 * `operator-bff:WS TransportVoucherChanged` (#797) — the PNR group's Uber-voucher
 * program changed (issued / codes landed / claimed / redeemed / cancelled). Fanned
 * into the `case:<urn>` room; the payload carries the WHOLE `GroupVoucherView`, so
 * the merge is a plain replace under `groupVoucherKey(subCaseUrn)` and duplicate
 * delivery (write echo today, Kafka later) lands on identical state. Named apart
 * from the MEAL-voucher vocabulary on purpose.
 */
export interface TransportVoucherChangedEvent {
  type: 'TransportVoucherChanged';
  caseUrn: string;
  subCaseUrn: string;
  voucher: GroupVoucherView;
}

/**
 * MASS ground transport ("Send to hotels") — one shuttle RUN changed (planned ·
 * requested · vehicle assigned · boarding · departed · arrived · cancelled · failed,
 * and every boarding event re-pushes the run's seat counters). Fanned into the
 * `disruption:<urn>` room; the payload is the WHOLE `ShuttleRunView`, so a Kafka
 * echo + a write echo land on identical state.
 */
export interface TransferRunChangedEvent {
  type: 'TransferRunChanged';
  disruptionUrn: string;
  run: ShuttleRunView;
}

/** The transfer PLAN changed (re-cut, new hotel enrolled, counters coalesced ≤1/2 s). */
export interface TransferBoardChangedEvent {
  type: 'TransferBoardChanged';
  disruptionUrn: string;
  plan: TransferPlanView;
}

/**
 * Care desk (`operator-bff:WS CareChanged@v1`, feat/care-desk K4/K5) — ONE PNR
 * group's care state changed (wallet card-issued / card-adjusted Kafka ingress,
 * or a care write's post-write echo). `card` is a PARTIAL slice by design
 * (`{ status?, currency?, faceValueMinor?, availableMinor?, lastAdjustment? }`)
 * — the merge patches what it gets for instant UI and invalidates the board for
 * the full truth, so at-least-once redelivery lands on identical state.
 */
export interface CareChangedEvent {
  type: 'CareChanged';
  disruptionUrn: string;
  subCaseUrn: string;
  card?: {
    urn?: string;
    status?: string;
    currency?: string;
    faceValueMinor?: number;
    availableMinor?: number;
    lastAdjustment?: { direction?: string; amountMinor?: number; loadUrn?: string; at?: string };
  };
}

/**
 * The disruption's account (`operator-bff:WS NarrativeChanged@v1`,
 * feat/disruption-why W6) — ONE narrative chapter landed, or was re-narrated,
 * on a disruption. Fanned into the `disruption:<urn>` room that Tower and
 * Express already join (and that the "¿Por qué?" dialog joins for itself when
 * opened from a case).
 *
 * `chapter` carries the WHOLE chapter slice when the producer has it — the
 * merge upserts by `chapterId`, so at-least-once redelivery lands on identical
 * state. Without it the push is a bare "this one changed" nudge and the account
 * is marked stale instead; either way this is the ONLY refresh mechanism for
 * the account. There is no interval and no timer in this feature.
 */
export interface NarrativeChangedEvent {
  type: 'NarrativeChanged';
  disruptionUrn: string;
  chapterId: string;
  chapter?: Record<string, unknown>;
}

/**
 * passenger-profile-completion: a party completed its profile from the counter QR
 * (or an operator corrected the contact). Emitted by the operator-bff next to
 * `CaseChanged('passenger')`; carries WHO/WHEN, never the data — the roster /
 * passengers refetch brings the new stamp + contact presence.
 */
export interface ProfileCompletedEvent {
  type: 'ProfileCompleted';
  caseUrn: string;
  subCaseUrn: string;
  at: string;
  source?: 'PASSENGER_SELF_SERVICE' | 'OPERATOR';
}

/**
 * Flight delay (`operator-bff:WS FlightDelayChanged@v1` / `FlightEscalated@v1`,
 * flight-delay B1 → U1) — one case's delay-timeline revision, or its escalation to a
 * cancellation, fanned into the `disruption:<urn>` room. The envelope carries the
 * WHOLE event payload keyed by `revision` / `escalationSeq`, so redelivery lands on
 * identical state. Shapes live in `lib/delay/types.ts`; the merge in `lib/delay/merge.ts`.
 */
export type { FlightDelayChangedEvent, FlightEscalatedEvent };

/**
 * `operator-bff:WS OperatorAlert@v1` (operator-notifications B1/U1) — a flight delay /
 * cancellation / diversion or a new disruption in the operator's airports, routed by the
 * BFF to the `alerts:airport:<IATA>` rooms the socket joined at handshake (scope is the
 * server's). Shape in `lib/alerts/alert.types.ts`; the alert host owns it end to end.
 */
export type { OperatorAlertEvent };

export type WsEvent =
  | FlightDelayChangedEvent
  | FlightEscalatedEvent
  | NarrativeChangedEvent
  | TransferRunChangedEvent
  | TransferBoardChangedEvent
  | CareChangedEvent
  | CompensationChangedEvent
  | StateChangedEvent
  | PassengerStatusChangedEvent
  | AllocationProgressEvent
  | BookingConfirmedEvent
  | CaseChangedEvent
  | ProfileCompletedEvent
  | DisruptionProgressChangedEvent
  | DisruptionAllocationPlanChangedEvent
  | ProviderStatusChangedEvent
  | IntegrationChangedEvent
  | FlightStatusChangedEvent
  | TripChangedEvent
  | TransportVoucherChangedEvent
  | OperatorNotificationEvent
  | OperatorAlertEvent;

type CaseOverviewCache = Record<string, unknown> & {
  locked_by?: LockState['holder'];
  lock_expires_at?: LockState['expiresAt'];
};

/** See the DisruptionProgressChanged branch — push-driven board refresh, debounced. */
const OVERVIEW_REFRESH_DEBOUNCE_MS = 2_500;
let overviewInvalidateTimer: ReturnType<typeof setTimeout> | null = null;
function scheduleOverviewInvalidate(qc: QueryClient): void {
  if (overviewInvalidateTimer !== null) return;
  overviewInvalidateTimer = setTimeout(() => {
    overviewInvalidateTimer = null;
    void qc.invalidateQueries({ queryKey: ['disruptions', 'overview'] });
  }, OVERVIEW_REFRESH_DEBOUNCE_MS);
}

/**
 * See the CareChanged branch. A bulk grant is one push PER PNR — the care board goes
 * stale on each (an inactive board re-reads on its next mount) but the OPEN board
 * re-reads once per burst, trailing, instead of cancelling and restarting its read on
 * every push.
 */
export const CARE_REFRESH_COALESCE_MS = 750;
const careRefetchTimers = new Map<string, ReturnType<typeof setTimeout>>();
function scheduleCareRefetch(qc: QueryClient, disruptionUrn: string): void {
  const queryKey = ['disruptions', disruptionUrn, 'care'];
  void qc.invalidateQueries({ queryKey, refetchType: 'none' });
  if (careRefetchTimers.has(disruptionUrn)) return;
  careRefetchTimers.set(
    disruptionUrn,
    setTimeout(() => {
      careRefetchTimers.delete(disruptionUrn);
      void qc.refetchQueries({ queryKey, type: 'active' });
    }, CARE_REFRESH_COALESCE_MS),
  );
}

/** The reservations this session saw END (§5 `endedReservationUrn`), keyed by URN. */
export const ENDED_RESERVATIONS = ['work', 'ended-reservations'] as const;

/**
 * See the CaseChanged('booking') branch: a burst of ended reservations (the acceptance
 * sweep releasing orphan holds) is ONE re-read of the rosters that hold those PNRs,
 * trailing — late enough for the cases denorm to have applied the event.
 */
export const ROSTER_REREAD_MS = 1_500;
const rosterReread: { timer: ReturnType<typeof setTimeout> | null; subs: Set<string> } = {
  timer: null,
  subs: new Set(),
};
function scheduleRosterReread(qc: QueryClient, subCaseUrn: string): void {
  rosterReread.subs.add(subCaseUrn);
  if (rosterReread.timer !== null) return;
  rosterReread.timer = setTimeout(() => {
    const subs = rosterReread.subs;
    rosterReread.timer = null;
    rosterReread.subs = new Set();
    void qc.invalidateQueries({
      predicate: (q) =>
        isPnrRosterQuery(q) &&
        !!(q.state.data as InfiniteData<RosterPage> | undefined)?.pages?.some((p) =>
          p.items?.some((g) => subs.has(g.subCaseUrn)),
        ),
    });
  }, ROSTER_REREAD_MS);
}

export function applyEvent(qc: QueryClient, event: WsEvent): void {
  switch (event.type) {
    case 'StateChanged': {
      const lock: LockState = {
        entityUrn: event.caseUrn,
        holder: event.payload.locked_by ?? null,
        expiresAt: event.payload.lock_expires_at ?? null,
      };
      qc.setQueryData(['cases', event.caseUrn, 'lock'], lock);
      qc.setQueryData<CaseOverviewCache>(
        ['cases', event.caseUrn, 'overview'],
        (prev) => ({
          ...(prev ?? {}),
          ...event.payload,
        }),
      );
      return;
    }
    case 'PassengerStatusChanged': {
      qc.setQueryData<Record<string, unknown>>(
        ['cases', event.caseUrn, 'passengers', event.passengerUrn],
        (prev) => ({ ...(prev ?? {}), ...event.payload }),
      );
      return;
    }
    case 'AllocationProgress': {
      qc.setQueryData<Record<string, unknown>>(
        ['cases', event.caseUrn, 'allocation'],
        (prev) => ({ ...(prev ?? {}), ...event.payload }),
      );
      return;
    }
    case 'BookingConfirmed': {
      qc.setQueryData<Record<string, unknown>>(
        ['cases', event.caseUrn, 'reservations', event.reservationUrn],
        (prev) => ({ ...(prev ?? {}), ...event.payload, status: 'confirmed' }),
      );
      // Allocation tab also wants to know — append to its timeline if cached.
      qc.setQueryData<{ confirmedReservations?: string[] } & Record<string, unknown>>(
        ['cases', event.caseUrn, 'allocation'],
        (prev) => {
          const existing = prev?.confirmedReservations ?? [];
          if (existing.includes(event.reservationUrn)) return prev ?? {};
          return {
            ...(prev ?? {}),
            confirmedReservations: [...existing, event.reservationUrn],
          };
        },
      );
      return;
    }
    case 'CaseChanged': {
      // A write by ANOTHER operator (or this one) landed at the BFF. We don't
      // get the new state — we invalidate ONLY the slice that changed so its
      // active query refetches once (NEX-149 spirit: targeted, never a global
      // refetch). The keys map 1:1 to the case-detail tab hooks:
      //   booking    → reservations  (use-reservations.ts)
      //   rebooking  → rebooking     (use-rebooking.ts)
      //   allocation → reservations + allocation (book-all touches both)
      //   passenger  → passengers    (use-passengers.ts)
      // overview is invalidated for every kind so summary counters refresh.
      const base = ['cases', event.caseUrn] as const;
      const invalidate = (key: readonly unknown[]): void => {
        void qc.invalidateQueries({ queryKey: key });
      };
      switch (event.kind) {
        case 'booking':
          invalidate([...base, 'reservations']);
          // Live-patch the PNR's roster row hotel denorm so the GRID reflects the
          // reservation immediately (the drawer matches reservations by subCaseUrn;
          // the grid reads the row's hotelReservationUrn, denormed async by cases).
          if (event.subCaseUrn && event.reservationUrn) {
            patchRosterHotelDenorm(
              qc,
              event.subCaseUrn,
              event.reservationUrn,
              event.hotelUrn,
              event.reservationStatus,
            );
          } else if (
            event.subCaseUrn &&
            event.reservationStatus &&
            RESERVATION_GONE.has(event.reservationStatus)
          ) {
            const ended = event.endedReservationUrn;
            if (ended) {
              // the row clears only while it still holds THAT reservation; «Nexa
              // trabajando» turns the run's ✓ for it into ✕ (it fell through)
              patchRosterHotelDenorm(qc, event.subCaseUrn, ended, undefined, event.reservationStatus);
              qc.setQueryData<Record<string, true>>(ENDED_RESERVATIONS, (prev) => ({
                ...prev,
                [ended]: true,
              }));
            } else {
              // An older BFF names no reservation: the row is left as it is and the
              // rosters holding that PNR re-read the cases denorm (which clears only a
              // MATCHING reservation) — trailing-coalesced, so a release sweep is one read.
              scheduleRosterReread(qc, event.subCaseUrn);
            }
          }
          break;
        case 'rebooking':
          invalidate([...base, 'rebooking']);
          break;
        case 'allocation':
          invalidate([...base, 'reservations']);
          invalidate([...base, 'allocation']);
          break;
        case 'passenger':
          invalidate([...base, 'passengers']);
          // transport-desk (T7): passenger-grain denorms (the hotel check-in
          // state) ALSO ride the DISRUPTION roster rows — mark every cached
          // disruption roster stale so the Journeys check-in loop advances live.
          // Same predicate the hotel-denorm patch uses; active queries refetch
          // once, inactive ones just flip stale (no fetch storm).
          void qc.invalidateQueries({
            predicate: (q) => q.queryKey[0] === 'disruptions' && q.queryKey[2] === 'roster',
          });
          break;
      }
      invalidate([...base, 'overview']);
      return;
    }
    case 'ProfileCompleted': {
      // passenger-profile-completion: the stamp + contact presence ride the roster
      // group rows AND the case passengers page — refresh both, pushed, never polled.
      void qc.invalidateQueries({ queryKey: ['cases', event.caseUrn, 'passengers'] });
      void qc.invalidateQueries({
        predicate: (q) => q.queryKey[0] === 'disruptions' && q.queryKey[2] === 'roster',
      });
      return;
    }
    case 'DisruptionProgressChanged': {
      // Live progress + run-state for a disruption, pushed over the WS (NEX-293, no poll).
      // The broadcaster only emits when its fingerprint CHANGED, so every push here means
      // real movement. Write it into the progress cache, then refresh the denorm views the
      // change affects — driven by the push, NOT a client timer.
      //
      // The /disruptions situation board joins the top rows' rooms and reads ONE
      // ['disruptions','overview'] query — any push means its numbers moved, so
      // refresh it too. Trailing-debounced: a chatty run refetches the board at
      // most once per window; still zero timers while the WS is quiet.
      scheduleOverviewInvalidate(qc);
      const urn = event.disruptionUrn;
      const prev = qc.getQueryData<DisruptionProgress>(['disruptions', urn, 'progress']);
      qc.setQueryData<DisruptionProgress>(['disruptions', urn, 'progress'], event.payload);

      // NEX-364 / U1 — fold the per-PNR settle outcomes carried on this push into
      // the rebooking-detail cache so the rich dialog rows fill in live over the WS
      // (no poll). Additive: degrades to a no-op when the run marker carries none.
      mergeSettleOutcomesIntoDetail(qc, urn, event.payload.settledGroups);

      const terminal =
        event.payload.runStatus === 'COMPLETED' ||
        event.payload.runStatus === 'PARTIAL' ||
        event.payload.runStatus === 'FAILED';
      // Async hotel bookings settle AFTER the run reports COMPLETED — the broadcaster now
      // carries `roomsCommitted`, so each settlement re-pushes with a higher value. THAT
      // push (not a setTimeout) is what refreshes the occupancy board.
      const hotelsSettled =
        prev != null && (prev.roomsCommitted ?? 0) !== (event.payload.roomsCommitted ?? 0);

      if (terminal || hotelsSettled) {
        // The rollup header (REBOOKED / HOTELS / COST), the occupancy board, the cost
        // breakdown, and the per-PNR hotel chips on the roster are all run-mutated denorm
        // views. (We don't invalidate on every RUNNING tick — only terminal or a settle.)
        void qc.invalidateQueries({ queryKey: ['disruptions', urn], exact: true });
        void qc.invalidateQueries({ queryKey: ['disruptions', urn, 'hotel-board'] });
        void qc.invalidateQueries({ queryKey: ['disruptions', urn, 'cost-breakdown'] });
        void qc.invalidateQueries({ queryKey: ['disruptions', urn, 'roster'] });
      }
      return;
    }
    case 'DisruptionAllocationPlanChanged': {
      // Another operator built/booked the SHARED allocation plan. Write the pushed
      // envelope straight into the allocation-plan cache — no refetch (NEX-149).
      // The key matches useDisruptionAllocationPlan exactly, so the Hotels tab
      // re-renders with the new plan + "Built/Booked by X" attribution live.
      qc.setQueryData<AllocationPlanEnvelope>(
        ['disruptions', event.disruptionUrn, 'allocation-plan'],
        event.payload,
      );
      return;
    }
    case 'ProviderStatusChanged': {
      // Live per-vendor health (D). Write the pushed snapshot straight into the
      // provider-status cache — the dedicated page re-renders, no refetch/poll.
      qc.setQueryData(['providers', 'status'], event.payload);
      return;
    }
    case 'IntegrationChanged': {
      // The event names what moved, not the new row: one re-read of the inventory
      // (+ routing for a toggle / routing save). Push-driven — no timer, no poll.
      applyIntegrationChanged(qc, event);
      return;
    }
    case 'flight.status-changed': {
      // Live flight-status push (NEX-343 / F5). Patch just this flight's row in
      // every cached monitor walk — replaces the F4 30s poll. The delta is the
      // event minus its routing keys (`type`, `flightUrn`, trace context).
      const { flightUrn } = event;
      const delta: Record<string, unknown> = { ...event };
      delete delta.type;
      delete delta.flightUrn;
      delete delta.correlationUrn;
      patchFlightMonitorRow(qc, flightUrn, delta as Partial<IFlightMonitorRow>);
      return;
    }
    case 'TripChanged': {
      // Live ground-transport state (feat/transport). Fold the pushed TripView
      // into the case's trip list — never a refetch (TDS §14). Replaces the trip
      // with the same urn, appends one we hadn't seen (an AUTOMATIC dispatch that
      // fired while the drawer was open), and keeps the list in its stable order.
      //
      // Only writes into an EXISTING cache entry: with no drawer open there is no
      // list to patch, and seeding one here would leave a partial, never-fetched
      // list behind for the next drawer to render from.
      if (!event.trip?.urn) return;
      const key = caseTripsKey(event.caseUrn);
      if (qc.getQueryData<TripView[]>(key) !== undefined) {
        qc.setQueryData<TripView[]>(key, (prev) => mergeTrip(prev, event.trip));
      }
      // transport-desk (T3): when the trip knows its disruption, ALSO upsert it
      // into the disruption-grain trips read the Transport tab seeded — same
      // never-seed discipline (an unopened tab has no page to patch, and `null`
      // means "route not wired on this BFF", which one push must not fake away).
      if (event.trip.disruptionUrn) mergeDisruptionTrip(qc, event.trip.disruptionUrn, event.trip);
      return;
    }
    case 'TransportVoucherChanged': {
      // The group's Uber voucher, live (#797). Whole-view replace under the group
      // key — never a refetch (TDS §14). Only writes into an EXISTING cache entry
      // (`null` = "none issued yet" counts as existing): with no drawer open there
      // is nothing to patch, and seeding here would leave a never-fetched entry
      // behind for the next drawer to trust.
      if (!event.voucher?.urn || !event.subCaseUrn) return;
      const key = groupVoucherKey(event.subCaseUrn);
      if (qc.getQueryData<GroupVoucherView | null>(key) !== undefined) {
        qc.setQueryData<GroupVoucherView | null>(key, event.voucher);
      }
      return;
    }
    case 'TransferRunChanged': {
      // Live shuttle run (MASS ground transport). Upsert the run into the cached
      // transfer board — never a refetch. Only writes into an EXISTING board (the
      // Hotels view / NOW card seeded it); the resolution-state aggregate is
      // re-read so the Transfers gate + the console follow the run.
      const urn = event.disruptionUrn;
      if (event.run?.urn) {
        mergeTransferRun(qc, urn, event.run);
        // transport-desk (T4): an OPEN run sheet re-reads this run's manifest —
        // the run push carries counters, not the seat list, so an invalidate (one
        // read, active query only) is the honest refresh. A closed sheet has no
        // active manifest query and nothing fires.
        void qc.invalidateQueries({
          queryKey: ['transport', 'run', event.run.urn, 'manifest'],
        });
      }
      void qc.invalidateQueries({ queryKey: ['disruptions', urn, 'resolution-state'] });
      return;
    }
    case 'TransferBoardChanged': {
      // The plan itself moved (re-cut / enrolled / coalesced counters): replace the
      // cached plan + refresh the per-hotel counters it carries; runs stay as the
      // run pushes left them.
      const urn = event.disruptionUrn;
      if (event.plan) mergeTransferPlan(qc, urn, event.plan);
      void qc.invalidateQueries({ queryKey: ['disruptions', urn, 'resolution-state'] });
      return;
    }
    case 'CareChanged': {
      // Care desk live half (feat/care-desk K5). Patch the cached board with the
      // partial card slice (instant chip flips: FAILED→ACTIVE, "settling" off),
      // then mark the care key stale so an ACTIVE query refetches the full board
      // truth (toppedUpMinor, facts, per-currency money) — push-driven, no poll; a
      // burst (a bulk grant) is ONE trailing re-read (`scheduleCareRefetch`).
      const urn = event.disruptionUrn;
      if (event.card) mergeCareCard(qc, urn, event.subCaseUrn, event.card);
      scheduleCareRefetch(qc, urn);
      // flight-delay U1: a card movement may be a delay-care entitlement being applied —
      // an OPEN entitlements list holding this group re-reads once (no wallet push of its own).
      invalidateEntitlementsOfGroup(qc, event.subCaseUrn);
      return;
    }
    case 'FlightDelayChanged': {
      // Flight delay, live (flight-delay U1). Patch the case's timeline, the disruption's
      // timelines (header badge) and every cached row's `flightStatus@v1` — guarded by
      // the payload's revision, never seeding a partial timeline. An OPEN delay panel
      // re-reads its case once per revision (the payload has no history row). No timer.
      applyFlightDelayChanged(qc, event);
      // flight-delay U5: the revision's escalation COUNTS (reassigned / released /
      // conflicts) that the cached URN lists cannot express yet → that timeline and the
      // case's reassignment badges re-read once (open ones only). After U1's merge.
      applyEscalationProgress(qc, event);
      return;
    }
    case 'FlightEscalated': {
      // The delayed flight became a cancellation. U1 writes the escalation into the
      // timelines and the rows' `flightStatus` (escalated, `graceUntil`) — which is what
      // shows the header alert and its countdown live (U5); U5 then marks stale what the
      // push cannot carry: a timeline we had read as absent, a list that lacks the case,
      // and the case's reassignment badges (the airline's window just opened).
      applyFlightEscalated(qc, event);
      applyEscalationArrived(qc, event);
      return;
    }
    case 'CompensationChanged': {
      // Regulatory compensation, live (flight-delay U3; `operator-bff:WS
      // CompensationChanged@v1`). Patch every cached list of the disruption (current +
      // history) with what the push carries, ordered by revision then `changedAt`, so
      // redelivery lands on identical state. A change the push cannot express in full
      // (an edit's reason, a new revision, a supersede) asks for ONE coalesced re-read
      // per burst — push-driven, never a poll. No cached list → nothing to do.
      const urn = event.disruptionUrn;
      let complete = true;
      qc.setQueriesData<ICompensationProposal[]>({ queryKey: compensationRoot(urn) }, (list) => {
        const merged = mergeCompensationChange(list, event.payload);
        complete &&= merged.complete;
        return merged.list;
      });
      if (!complete) scheduleCompensationRefresh(qc, urn);
      return;
    }
    case 'NarrativeChanged': {
      // The account, live (feat/disruption-why W6). Fold the landed chapter into
      // EVERY cached view of this disruption — the disruption-scoped one and any
      // case-scoped one, since a case chapter is content INSIDE the disruption's
      // account and both surfaces must flip together.
      //
      // A malformed payload is dropped by `toChapter` rather than rendered: the
      // dialog must never show a half-parsed chapter as if it were the record.
      const urn = event.disruptionUrn;
      const of = (q: { queryKey: readonly unknown[]; state: { data: unknown } }): boolean =>
        isWhyQueryOfDisruption(q.queryKey, q.state.data, urn);
      const chapter = toChapter(event.chapter);
      if (chapter) {
        qc.setQueriesData<WhyView>({ predicate: of }, (view) => upsertChapter(view, chapter));
        return;
      }
      // Bare nudge: we know WHICH chapter moved but not what it says. Mark the
      // account stale — an OPEN dialog re-reads once, a closed one only flips
      // stale and fetches nothing. Push-driven, no timer, no poll.
      void qc.invalidateQueries({ predicate: of });
      return;
    }
    case 'OperatorAlert':
      // No cache consequence: an alert is news ABOUT data, not data. The flight / the
      // disruption it names moves through its own push (flight.status-changed,
      // DisruptionProgressChanged…); the alert host (lib/alerts) decides how to say it.
      return;
    case 'OperatorNotification': {
      // Global run-finished notification (NEX-296). The operator-facing surface —
      // the toast + the bell — is raised by WsEventBridge; what belongs HERE is the
      // cache consequence, which only exists because this event is global: the run
      // mutated the disruption's denorm views while the operator was on ANOTHER
      // screen, where no `DisruptionProgressChanged` reached them (that one is
      // room-gated). Mark those views stale so a walk back to the disruption shows
      // the finished run. Inactive queries only flip stale — no fetch, no storm;
      // if they ARE on that disruption, the progress push already refreshed it and
      // TanStack collapses the duplicate.
      if (event.payload.kind !== 'run') return; // v1 discriminator — ignore future kinds
      const urn = event.payload.run.disruptionUrn;
      void qc.invalidateQueries({ queryKey: ['disruptions', urn], exact: true });
      void qc.invalidateQueries({ queryKey: ['disruptions', urn, 'progress'] });
      void qc.invalidateQueries({ queryKey: ['disruptions', urn, 'roster'] });
      void qc.invalidateQueries({ queryKey: ['disruptions', urn, 'hotel-board'] });
      void qc.invalidateQueries({ queryKey: ['disruptions', urn, 'cost-breakdown'] });
      return;
    }
  }
}

// Type guard for runtime events arriving from the socket. The BFF emits events
// matching the WsEvent shape; this guard rejects malformed payloads silently
// (logged at the call site).
/**
 * Shape-check the `operator-notification@v1` envelope. Only the fields the UI
 * renders are required; `kind` is checked as *a string*, not as `'run'`, so a
 * future v1 kind still arrives intact and is skipped by the consumer's explicit
 * `kind` switch instead of being silently dropped as "malformed".
 */
function isOperatorNotificationV1(value: unknown): value is IOperatorNotificationV1 {
  if (!value || typeof value !== 'object') return false;
  const n = value as Partial<IOperatorNotificationV1>;
  if (typeof n.id !== 'string' || typeof n.kind !== 'string') return false;
  if (typeof n.title !== 'string' || typeof n.message !== 'string') return false;
  if (n.severity !== 'success' && n.severity !== 'warning' && n.severity !== 'error') return false;
  return !!n.run && typeof n.run === 'object' && typeof n.run.disruptionUrn === 'string';
}

export function isWsEvent(value: unknown): value is WsEvent {
  if (!value || typeof value !== 'object') return false;
  const v = value as {
    type?: unknown;
    caseUrn?: unknown;
    disruptionUrn?: unknown;
    operatorUrn?: unknown;
    payload?: unknown;
  };
  if (typeof v.type !== 'string') return false;
  // Disruption-scoped events are keyed by disruptionUrn + carry a payload.
  if (v.type === 'DisruptionProgressChanged' || v.type === 'DisruptionAllocationPlanChanged') {
    return typeof v.disruptionUrn === 'string' && !!v.payload && typeof v.payload === 'object';
  }
  // MASS ground transport — disruption-keyed, payload rides on `run` / `plan`.
  if (v.type === 'TransferRunChanged') {
    const run = (v as { run?: { urn?: unknown } }).run;
    return typeof v.disruptionUrn === 'string' && !!run && typeof run === 'object' && typeof run.urn === 'string';
  }
  if (v.type === 'TransferBoardChanged') {
    const plan = (v as { plan?: { hotels?: unknown } }).plan;
    return typeof v.disruptionUrn === 'string' && !!plan && typeof plan === 'object';
  }
  // Care desk (feat/care-desk K5) — disruption-keyed; `card` is optional (a bare
  // "something changed" nudge is valid), but the sub-case key must be present.
  if (v.type === 'CareChanged') {
    return (
      typeof v.disruptionUrn === 'string' &&
      typeof (v as { subCaseUrn?: unknown }).subCaseUrn === 'string'
    );
  }
  // Regulatory compensation (flight-delay U3) — disruption-keyed; the merge folds the
  // payload by proposal URN, so the URN, the payload and its revision are required.
  if (v.type === 'CompensationChanged') {
    const p = v.payload as { proposalUrn?: unknown; revision?: unknown; status?: unknown } | undefined;
    return (
      typeof v.disruptionUrn === 'string' &&
      typeof (v as { proposalUrn?: unknown }).proposalUrn === 'string' &&
      !!p &&
      typeof p === 'object' &&
      typeof p.proposalUrn === 'string' &&
      typeof p.revision === 'number' &&
      typeof p.status === 'string'
    );
  }
  // The disruption's account (feat/disruption-why W6) — disruption-keyed; the
  // chapter payload is OPTIONAL (a bare nudge is valid), but the chapter id must
  // be present, since it is what the merge upserts by.
  if (v.type === 'NarrativeChanged') {
    return (
      typeof v.disruptionUrn === 'string' &&
      typeof (v as { chapterId?: unknown }).chapterId === 'string'
    );
  }
  // Flight delay (flight-delay U1) — disruption-room events keyed by disruption AND
  // case, carrying the revisioned / sequenced payload the merge indexes by.
  if (v.type === 'FlightDelayChanged') return isFlightDelayChanged(v as Record<string, unknown>);
  if (v.type === 'FlightEscalated') return isFlightEscalated(v as Record<string, unknown>);
  // Global provider-status event — keyed by neither urn, just a payload.
  if (v.type === 'ProviderStatusChanged') {
    return !!v.payload && typeof v.payload === 'object';
  }
  if (v.type === 'IntegrationChanged') return readIntegrationChange(v) !== null;
  // Live flight-status push (NEX-343) — keyed by `flightUrn`, flat delta.
  if (v.type === 'flight.status-changed') {
    return typeof (v as { flightUrn?: unknown }).flightUrn === 'string';
  }
  // Global operator notification (NEX-296) — keyed by `operatorUrn`, carrying the
  // v1 envelope. Validated on the fields the UI actually renders, so a malformed
  // push is dropped at the door rather than toasting `undefined` at an operator.
  if (v.type === 'OperatorNotification') {
    return typeof v.operatorUrn === 'string' && isOperatorNotificationV1(v.payload);
  }
  // Operator alert (operator-notifications U1) — airport-routed, the v1 alert as payload.
  if (v.type === 'OperatorAlert') return isOperatorAlertV1(v.payload);
  // All case-scoped events are keyed by caseUrn.
  if (typeof v.caseUrn !== 'string') return false;
  // Ground transport (feat/transport) — case-keyed, but its payload rides on
  // `trip`, not `payload`. Validated on the two fields the merge indexes by, so a
  // mangled push is dropped at the door instead of appending a blank row.
  if (v.type === 'TripChanged') {
    const trip = (v as { trip?: { urn?: unknown; status?: unknown } }).trip;
    return !!trip && typeof trip === 'object' && typeof trip.urn === 'string';
  }
  // PNR-group Uber voucher (#797) — case-keyed; payload rides on `voucher` and the
  // merge is keyed by `subCaseUrn`, so both are validated at the door.
  if (v.type === 'TransportVoucherChanged') {
    const voucher = (v as { voucher?: { urn?: unknown } }).voucher;
    return (
      typeof (v as { subCaseUrn?: unknown }).subCaseUrn === 'string' &&
      !!voucher &&
      typeof voucher === 'object' &&
      typeof voucher.urn === 'string'
    );
  }
  return [
    'StateChanged',
    'PassengerStatusChanged',
    'AllocationProgress',
    'BookingConfirmed',
    'CaseChanged',
    'ProfileCompleted',
  ].includes(v.type);
}
